import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from '../ui/sheet';
import { Menu } from 'lucide-react';

const navLinks = [
  { to: '/how-it-works', label: 'How It Works', testId: 'nav-how-it-works-link' },
  { to: '/pricing', label: 'Pricing', testId: 'nav-pricing-link' },
  { to: '/calculator', label: 'ROI Calculator', testId: 'nav-calculator-link' },
  { to: '/about', label: 'About', testId: 'nav-about-link' },
  { to: '/contact', label: 'Contact', testId: 'nav-contact-link' },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/70 border-b border-[hsl(var(--border))]"
    >
      <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 group shrink-0" data-testid="nav-home-link">
          <img
            src="/logo.png"
            alt="Smile Score Solutions"
            className="h-10 w-auto"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-4 lg:gap-6">
          {navLinks.map((link) => (
            <Link
              key={link.to}
              to={link.to}
              data-testid={link.testId}
              className={`text-sm font-medium transition-colors ${
                location.pathname === link.to
                  ? 'text-[hsl(var(--sss-navy))]'
                  : 'text-[hsl(var(--sss-navy))]/70 hover:text-[hsl(var(--sss-navy))]'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Desktop CTAs */}
        <div className="hidden md:flex items-center gap-3">
          <a
            href="https://approvemysmile.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-medium text-[hsl(var(--sss-navy))]/70 hover:text-[hsl(var(--sss-navy))] transition-colors hidden lg:inline mr-2"
            data-testid="header-login-link"
          >
            Login
          </a>
          <Link to="/contact">
            <Button
              data-testid="header-book-demo-button"
              className="bg-[hsl(var(--sss-coral))] hover:bg-[hsl(var(--sss-coral-2))] text-white rounded-xl px-5 h-9 text-sm font-medium shadow-sm"
            >
              Book a Demo
            </Button>
          </Link>
          <a href="https://approvemysmile.com/register/personal" target="_blank" rel="noopener noreferrer">
            <Button
              data-testid="header-get-started-button"
              variant="outline"
              className="border-[hsl(var(--sss-navy))] text-[hsl(var(--sss-navy))] hover:bg-[hsl(var(--sss-navy))] hover:text-white rounded-xl px-5 h-9 text-sm font-medium"
            >
              Get Started
            </Button>
          </a>
        </div>

        {/* Mobile menu */}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button variant="ghost" size="icon" data-testid="mobile-menu-open-button">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-72 bg-white pt-12">
            <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
            <SheetDescription className="sr-only">Main site navigation links</SheetDescription>
            <nav className="flex flex-col gap-4">
              <Link
                to="/"
                onClick={() => setOpen(false)}
                className="text-base font-medium text-[hsl(var(--sss-navy))] py-2"
              >
                Home
              </Link>
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setOpen(false)}
                  className={`text-base font-medium py-2 ${
                    location.pathname === link.to
                      ? 'text-[hsl(var(--sss-coral))]'
                      : 'text-[hsl(var(--sss-navy))]'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <a
                href="https://approvemysmile.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-base font-medium text-[hsl(var(--sss-navy))] py-2"
              >
                Login
              </a>
              <div className="flex flex-col gap-3 mt-4 pt-4 border-t border-[hsl(var(--border))]">
                <Link to="/contact" onClick={() => setOpen(false)}>
                  <Button className="w-full bg-[hsl(var(--sss-coral))] hover:bg-[hsl(var(--sss-coral-2))] text-white rounded-xl">
                    Book a Demo
                  </Button>
                </Link>
                <a href="https://approvemysmile.com/register/personal" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full border-[hsl(var(--sss-navy))] text-[hsl(var(--sss-navy))] rounded-xl">
                    Get Started
                  </Button>
                </a>
              </div>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};
